/* ============================================================================
   CHANVRERY — CHAT « ERDY » : LE SOCLE
   ============================================================================

   SOURCE : design/REFERENCE.md (relevé Figma, page 221:6056) et planches.json.
   Familles couvertes ici : A1–A7 (lanceur), B1–B6 (contenants), C5 (remise à
   zéro). Les familles D, E, F2–F9, G et H appartiennent aux modules.

   RÔLE DE CE FICHIER
   Il ne parle PAS au serveur, il ne dessine PAS de message, il ne touche PAS au
   panier. Il tient trois choses, et rien d'autre :

     1. la machine à états du widget   (fermé · accroche · ouvert · réduit)
     2. les identifiants de session    (id_visiteur, id_conversation)
     3. un registre + un bus           où les trois autres modules se branchent

   POURQUOI UN REGISTRE PLUTÔT QUE DES IMPORTS
   Le thème n'a pas d'étape de compilation : pas de bundler, pas de modules ES,
   pas de résolution de dépendances. Trois fichiers chargés en `defer` doivent
   pouvoir s'ignorer mutuellement et néanmoins se trouver. Le registre est la
   forme la plus simple qui tienne : chacun se déclare, personne n'importe.

   ORDRE DE CHARGEMENT (imposé par sections/ch-chat-lea.liquid, `defer` conserve
   l'ordre du document) :
     ch-chat.js            ce fichier — définit window.ChanvreryChat
     ch-chat-transport.js  réseau, SSE
     ch-chat-rendu.js      bulles, chips, états
     ch-chat-panier.js     cartes produit, ajout au panier

   L'ÉVÉNEMENT « pret » EST RÉMANENT
   Un module chargé après la connexion de l'élément recevrait un `pret` déjà
   passé et resterait muet. `ecouter('pret', …)` rejoue donc la dernière
   émission au nouvel abonné. C'est le seul événement qui se comporte ainsi ;
   sans lui, l'ordre de chargement deviendrait un piège silencieux.

   AUCUN TEXTE FRANÇAIS N'EST ÉCRIT ICI.
   Tous les libellés viennent du bloc <script type="application/json"
   data-ch-chat-textes> rendu par la section, donc du Liquid, donc modifiables
   sans toucher au JavaScript. `ChanvreryChat.textes()` les expose.
   ========================================================================= */

(function () {
  'use strict';

  /* Le fichier peut être évalué deux fois (section dupliquée dans l'éditeur de
     thème, rechargement partiel de section). Une seule définition suffit. */
  if (window.ChanvreryChat) return;

  /* ==========================================================================
     1. CONSTANTES
     ====================================================================== */

  /** États du widget. Un seul est actif à la fois. */
  var ETATS = {
    /** Panneau clos, seul le lanceur est visible — planche A1. */
    FERME: 'ferme',
    /** Bulle d'accroche proactive affichée à gauche du lanceur — planche A3. */
    ACCROCHE: 'accroche',
    /** Panneau déployé — planches A6, B1, B2, B3. */
    OUVERT: 'ouvert',
    /** Panneau replié sur son en-tête — planche B4. */
    REDUIT: 'reduit',
  };

  /** Tailles du panneau desktop. Orthogonal à l'état — planches B2 et B3. */
  var TAILLES = {
    /** 400 × 620 — planche B2, valeur par défaut. */
    COMPACTE: 'compacte',
    /** 640 × 720 — planche B3, obtenue par le bouton « agrandir ». */
    LARGE: 'large',
  };

  /* Clés de stockage. Préfixe `ch_` comme le reste du thème (cf. ch-overlay.js
     qui pose ch_age et ch_consentement). */
  var CLE_VISITEUR = 'ch_chat_visiteur';
  var CLE_CONVERSATION = 'ch_chat_conversation';
  var COOKIE_ACCROCHE = 'ch_chat_accroche';

  /** Plafond d'affichage de la pastille de non-lus — planche A2 : « 9+ ». */
  var PLAFOND_NON_LUS = 9;

  /* ==========================================================================
     2. STOCKAGE TOLÉRANT AUX PANNES
     ======================================================================
     Safari en navigation privée, un navigateur avec les cookies tiers coupés,
     un `localStorage` plein : `getItem` lève. Une exception ici rendrait le
     widget entièrement muet alors que la conversation pourrait très bien se
     tenir sans persistance. On retombe donc sur une mémoire de page.
     ====================================================================== */

  var memoire = {};

  function lireStockage(zone, cle) {
    try {
      var valeur = window[zone].getItem(cle);
      if (valeur) return valeur;
    } catch (e) {
      /* Stockage refusé : on lit la mémoire de repli ci-dessous. */
    }
    return memoire[cle] || '';
  }

  function ecrireStockage(zone, cle, valeur) {
    memoire[cle] = valeur;
    try {
      window[zone].setItem(cle, valeur);
    } catch (e) {
      /* La valeur ne survivra pas au rechargement. La session en cours, si. */
    }
  }

  function lireCookie(nom) {
    try {
      var trouve = document.cookie.match(new RegExp('(?:^|;\\s*)' + nom + '=([^;]*)'));
      return trouve ? decodeURIComponent(trouve[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function ecrireCookie(nom, valeur, jours) {
    try {
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        nom +
        '=' +
        encodeURIComponent(valeur) +
        '; Max-Age=' +
        Math.round(jours * 86400) +
        '; Path=/; SameSite=Lax' +
        secure;
    } catch (e) {
      /* Idem : sans cookie, l'accroche reproposera au prochain chargement. */
    }
  }

  /* ==========================================================================
     3. IDENTIFIANTS
     ======================================================================
     ⚠️ CONTRAINTE SERVEUR, NON NÉGOCIABLE.
     src/http/validation.ts impose `z.string().uuid()` sur id_conversation ET
     sur id_visiteur : une chaîne libre produit un 400, jamais un tour. Toute
     valeur fabriquée ici DOIT être un UUID v4 bien formé.

     `crypto.randomUUID` n'existe qu'en contexte sécurisé et à partir de
     Safari 15.4 : le repli n'est pas de la précaution décorative, c'est ce qui
     évite un widget mort sur `shopify theme dev` (http://127.0.0.1).
     ====================================================================== */

  function genererUuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {
      /* Certains navigateurs exposent la fonction et lèvent hors HTTPS. */
    }

    var octets = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(octets);
    } else {
      for (var i = 0; i < 16; i++) octets[i] = Math.floor(Math.random() * 256);
    }

    /* Marquage de version (4) et de variante (RFC 4122), sinon la regex du
       serveur rejette la valeur. */
    octets[6] = (octets[6] & 0x0f) | 0x40;
    octets[8] = (octets[8] & 0x3f) | 0x80;

    var hex = [];
    for (var j = 0; j < 16; j++) hex.push((octets[j] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }

  /** Un UUID mal formé en stockage (édition manuelle, migration) est jeté. */
  var MOTIF_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  function identifiantPersistant(zone, cle) {
    var valeur = lireStockage(zone, cle);
    if (!MOTIF_UUID.test(valeur)) {
      valeur = genererUuid();
      ecrireStockage(zone, cle, valeur);
    }
    return valeur;
  }

  /* ==========================================================================
     4. BUS D'ÉVÉNEMENTS
     ====================================================================== */

  var abonnes = {};
  /* Dernière charge utile de `pret`, rejouée aux abonnés tardifs (cf. préambule). */
  var pretRemanent = null;

  function ecouter(nom, fonction) {
    if (typeof fonction !== 'function') return function () {};

    (abonnes[nom] = abonnes[nom] || []).push(fonction);

    if (nom === 'pret' && pretRemanent) {
      /* Asynchrone : l'abonné finit d'abord de s'initialiser. */
      window.setTimeout(function () {
        appeler(fonction, pretRemanent);
      }, 0);
    }

    return function desabonner() {
      var liste = abonnes[nom];
      if (!liste) return;
      var index = liste.indexOf(fonction);
      if (index !== -1) liste.splice(index, 1);
    };
  }

  function appeler(fonction, detail) {
    try {
      fonction(detail);
    } catch (e) {
      /* Un module qui échoue ne doit pas emporter les autres avec lui : le
         panier peut casser sans que la conversation cesse de s'afficher. */
      console.error('[Chanvrery · chat] abonné en erreur', e);
    }
  }

  function emettre(nom, detail) {
    var charge = detail || {};
    if (nom === 'pret') pretRemanent = charge;

    (abonnes[nom] || []).slice().forEach(function (fonction) {
      appeler(fonction, charge);
    });

    /* Doublon en événement DOM : permet à du code hors module (une section
       tierce, un pixel) d'écouter sans dépendre de window.ChanvreryChat. */
    document.dispatchEvent(new CustomEvent('ch-chat:' + nom, { detail: charge }));
  }

  /* ==========================================================================
     5. REGISTRE DES MODULES
     ====================================================================== */

  var modules = {};

  /**
   * Enregistre un module. Trois noms sont attendus : 'transport', 'rendu',
   * 'panier'. Un quatrième nom est accepté sans erreur mais personne ne
   * l'appellera.
   *
   * @param {string} nom
   * @param {object} module
   */
  function enregistrer(nom, module) {
    if (!nom || !module) return;
    modules[nom] = module;
    emettre('module', { nom: nom, module: module });
  }

  /**
   * Renvoie un module, ou `null` s'il n'est pas chargé.
   * ⚠️ TOUJOURS tester le retour : un fichier peut manquer (asset non déployé,
   * erreur de syntaxe qui a interrompu son évaluation) et le widget doit alors
   * dégrader, pas exploser.
   *
   * @param {string} nom
   * @returns {object|null}
   */
  function module(nom) {
    return modules[nom] || null;
  }

  /* ==========================================================================
     6. L'ÉLÉMENT <ch-chat-lea>
     ======================================================================
     Il possède la coquille : lanceur, accroche, en-tête, zone de saisie, pied
     légal. Il ne possède NI les bulles, NI les cartes produit, NI les états
     d'erreur — voir docs/CONTRAT.md.
     ====================================================================== */

  /* Références DOM résolues une seule fois et distribuées à tout le monde par
     l'événement `pret`. Aucun module ne doit refaire de querySelector sur la
     coquille : si un nom d'attribut change, il ne change qu'ici. */
  var SELECTEURS = {
    lanceur: '[data-ch-chat-lanceur]',
    badgeNonLus: '[data-ch-chat-non-lus]',
    accroche: '[data-ch-chat-accroche]',
    accrocheRejeter: '[data-ch-chat-accroche-rejeter]',
    panneau: '[data-ch-chat-panneau]',
    entete: '[data-ch-chat-entete]',
    titreReduit: '[data-ch-chat-titre-reduit]',
    corps: '[data-ch-chat-corps]',
    fil: '[data-ch-chat-fil]',
    chips: '[data-ch-chat-chips]',
    formulaire: '[data-ch-chat-form]',
    champ: '[data-ch-chat-champ]',
    envoyer: '[data-ch-chat-envoyer]',
    aide: '[data-ch-chat-aide]',
    compteur: '[data-ch-chat-compteur]',
    statut: '[data-ch-chat-statut]',
    legal: '[data-ch-chat-legal]',
  };

  var hote = null; /* L'élément <ch-chat-lea> actif. */
  var refs = {};
  var reglages = {};
  var textes = {};

  var etatCourant = ETATS.FERME;
  var tailleCourante = TAILLES.COMPACTE;
  var nonLus = 0;
  var minuterieAccroche = 0;
  var declencheurOuverture = null; /* Élément à re-focaliser à la fermeture. */

  /* `conversation_ouverte` compte des CONVERSATIONS, pas des clics : refermer
     et rouvrir le panneau ne doit pas gonfler le compteur de l'étage 1. Remis
     à faux par nouvelleConversation() seulement. */
  var conversationDeclaree = false;

  function estDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Applique l'état sur le DOM. Un attribut unique sur l'hôte, `data-etat` :
   * le CSS n'a qu'une chose à lire, et l'état reste inspectable dans le
   * navigateur sans ouvrir la console.
   */
  function appliquerEtat(nouvel, origine) {
    if (nouvel === etatCourant) return;

    var precedent = etatCourant;
    etatCourant = nouvel;
    if (hote) hote.dataset.etat = nouvel;

    var ouvert = nouvel === ETATS.OUVERT;

    if (refs.panneau) {
      /* `hidden` en plus des classes : sans lui, le panneau reste dans l'ordre
         de tabulation quand une transition CSS échoue à s'appliquer. */
      refs.panneau.hidden = nouvel === ETATS.FERME || nouvel === ETATS.ACCROCHE;
    }
    if (refs.lanceur) {
      refs.lanceur.setAttribute('aria-expanded', ouvert || nouvel === ETATS.REDUIT ? 'true' : 'false');
    }
    if (refs.accroche) {
      refs.accroche.hidden = nouvel !== ETATS.ACCROCHE;
    }

    emettre('etat', { etat: nouvel, precedent: precedent, origine: origine || null });
  }

  /* ==========================================================================
     7. API PUBLIQUE
     ====================================================================== */

  var ChanvreryChat = {
    ETATS: ETATS,
    TAILLES: TAILLES,

    /* --- Lecture ---------------------------------------------------------- */

    /** @returns {string} l'un de ChanvreryChat.ETATS. */
    etat: function () {
      return etatCourant;
    },

    /** @returns {string} l'un de ChanvreryChat.TAILLES. */
    taille: function () {
      return tailleCourante;
    },

    /**
     * Références DOM de la coquille. Vide tant que `pret` n'a pas été émis.
     * Clés disponibles : voir SELECTEURS ci-dessus.
     * @returns {Object.<string, HTMLElement>}
     */
    refs: function () {
      return refs;
    },

    /**
     * Réglages issus du {% schema %} de la section, déjà typés.
     * { urlServeur, langue, maxCaracteres, delaiAccroche, joursAccroche,
     *   accrocheProduitSeulement, typePage }
     * @returns {object}
     */
    reglages: function () {
      return reglages;
    },

    /**
     * Libellés français, lus du bloc JSON rendu par la section.
     * ⚠️ NE JAMAIS écrire de texte d'interface en dur dans un module : ajouter
     * la clé dans sections/ch-chat-lea.liquid et la lire ici.
     * @returns {object}
     */
    textes: function () {
      return textes;
    },

    /** @returns {HTMLElement|null} l'élément <ch-chat-lea>. */
    racine: function () {
      return hote;
    },

    /* --- Identifiants ------------------------------------------------------ */

    /**
     * UUID v4 du navigateur, persistant (localStorage).
     * Clé du quota horaire côté serveur. Ne sert JAMAIS à relire une
     * conversation : le serveur n'en garde qu'une empreinte salée (R3).
     * @returns {string}
     */
    idVisiteur: function () {
      return identifiantPersistant('localStorage', CLE_VISITEUR);
    },

    /**
     * UUID v4 de la conversation, persistant le temps de l'onglet
     * (sessionStorage). Un nouvel onglet = une nouvelle conversation.
     * @returns {string}
     */
    idConversation: function () {
      return identifiantPersistant('sessionStorage', CLE_CONVERSATION);
    },

    /**
     * Planche C5 — repart de zéro. Régénère l'identifiant de conversation,
     * vide le fil via le module de rendu, et émet `conversation-nouvelle`.
     * @returns {string} le nouvel identifiant.
     */
    nouvelleConversation: function () {
      var identifiant = genererUuid();
      ecrireStockage('sessionStorage', CLE_CONVERSATION, identifiant);
      conversationDeclaree = false;

      var rendu = module('rendu');
      if (rendu && typeof rendu.effacerFil === 'function') rendu.effacerFil();

      emettre('conversation-nouvelle', { idConversation: identifiant });
      return identifiant;
    },

    /* --- Machine à états ---------------------------------------------------- */

    /**
     * Ouvre le panneau.
     * Déclare `conversation_ouverte` au serveur une seule fois par
     * conversation — le compteur mesure des conversations, pas des clics.
     *
     * @param {string} [origine] 'lanceur' | 'accroche' | 'api'
     */
    ouvrir: function (origine) {
      if (etatCourant === ETATS.OUVERT) return;

      window.clearTimeout(minuterieAccroche);
      declencheurOuverture = document.activeElement;

      appliquerEtat(ETATS.OUVERT, origine || 'api');
      ChanvreryChat.marquerNonLus(0);

      /* Le focus entre dans le panneau : sans ça, la tabulation repartirait du
         haut de la page derrière. Le panneau n'est PAS modal (aria-modal reste
         faux) — la boutique reste utilisable pendant la conversation. */
      if (refs.champ) {
        window.requestAnimationFrame(function () {
          try {
            refs.champ.focus({ preventScroll: true });
          } catch (e) {
            refs.champ.focus();
          }
        });
      }

      if (!conversationDeclaree) {
        conversationDeclaree = true;
        var transport = module('transport');
        if (transport && typeof transport.declarer === 'function') {
          transport.declarer('conversation_ouverte');
        }
      }

      emettre('ouvert', { origine: origine || 'api' });
    },

    /** Referme le panneau et rend le focus au lanceur. */
    fermer: function () {
      if (etatCourant === ETATS.FERME) return;
      appliquerEtat(ETATS.FERME, 'api');

      var cible = declencheurOuverture && document.contains(declencheurOuverture) ? declencheurOuverture : refs.lanceur;
      declencheurOuverture = null;
      if (cible) cible.focus();

      emettre('ferme', {});
    },

    /** Planche B4 — replie le panneau sur son en-tête, la conversation vit. */
    reduire: function () {
      if (etatCourant !== ETATS.OUVERT) return;
      appliquerEtat(ETATS.REDUIT, 'api');
      emettre('reduit', {});
    },

    /** Redéploie un panneau réduit. */
    restaurer: function () {
      if (etatCourant !== ETATS.REDUIT) return;
      ChanvreryChat.ouvrir('api');
    },

    /** Ouvre si fermé ou réduit, ferme sinon. C'est ce que fait le lanceur. */
    basculer: function (origine) {
      if (etatCourant === ETATS.OUVERT) ChanvreryChat.fermer();
      else ChanvreryChat.ouvrir(origine);
    },

    /**
     * Planches B2 ↔ B3 — bascule 400 × 620 / 640 × 720.
     * Sans effet sous 750 px : le mobile est déjà en plein écran (B1).
     * @param {string} [taille] l'un de ChanvreryChat.TAILLES ; bascule si omis.
     */
    basculerTaille: function (taille) {
      if (!estDesktop()) return;

      tailleCourante = taille || (tailleCourante === TAILLES.COMPACTE ? TAILLES.LARGE : TAILLES.COMPACTE);
      if (hote) hote.dataset.taille = tailleCourante;
      emettre('taille', { taille: tailleCourante });
    },

    /* --- Accroche proactive (A3) -------------------------------------------- */

    /**
     * Affiche la bulle d'accroche. Sans effet si le panneau est ouvert, si
     * l'accroche a déjà été montrée dans cette session, ou si le visiteur l'a
     * rejetée dans les 7 derniers jours.
     */
    montrerAccroche: function () {
      if (etatCourant !== ETATS.FERME) return;
      if (lireCookie(COOKIE_ACCROCHE) === 'rejetee') return;
      if (memoire.accrocheVue) return;

      memoire.accrocheVue = true;
      appliquerEtat(ETATS.ACCROCHE, 'minuterie');
      emettre('accroche', {});
    },

    /**
     * Croix de la bulle — pose le cookie « ne plus proposer » et retombe en
     * état fermé. Durée en jours pilotée par le réglage de section (7 par
     * défaut, valeur du relevé).
     */
    rejeterAccroche: function () {
      ecrireCookie(COOKIE_ACCROCHE, 'rejetee', reglages.joursAccroche || 7);
      if (etatCourant === ETATS.ACCROCHE) appliquerEtat(ETATS.FERME, 'rejet');
      if (refs.lanceur) refs.lanceur.focus();
      emettre('accroche-rejetee', {});
    },

    /* --- Pastille de non-lus (A2) -------------------------------------------- */

    /**
     * @param {number} nombre valeur absolue, pas un incrément.
     *        Au-delà de 9, la pastille affiche « 9+ » (plafond du relevé).
     */
    marquerNonLus: function (nombre) {
      nonLus = Math.max(0, nombre | 0);
      if (!refs.badgeNonLus) return;

      refs.badgeNonLus.hidden = nonLus === 0;
      refs.badgeNonLus.textContent = nonLus > PLAFOND_NON_LUS ? PLAFOND_NON_LUS + '+' : String(nonLus);
    },

    /** @returns {number} */
    nonLus: function () {
      return nonLus;
    },

    /* --- Zone de saisie ------------------------------------------------------ */

    /**
     * Verrouille l'envoi. Appelé par le transport pendant un tour (F8) et
     * quand la connexion tombe (F9).
     *
     * @param {string} raison 'occupe' | 'hors_ligne' — sert à choisir le
     *        libellé d'aide dans `textes()`. Toute autre valeur verrouille
     *        sans changer le libellé.
     */
    verrouillerSaisie: function (raison) {
      if (hote) hote.dataset.saisie = raison || 'verrouillee';
      if (refs.champ) refs.champ.disabled = raison === 'hors_ligne';
      if (refs.envoyer) refs.envoyer.disabled = true;
      if (refs.aide && textes[raison]) refs.aide.textContent = textes[raison];
    },

    /** Rend la saisie au visiteur et restaure le libellé d'aide d'origine. */
    deverrouillerSaisie: function () {
      if (hote) delete hote.dataset.saisie;
      if (refs.champ) refs.champ.disabled = false;
      if (refs.aide && textes.aide) refs.aide.textContent = textes.aide;
      majEnvoi();
    },

    /** Vide le champ de saisie et replie sa hauteur automatique. */
    viderSaisie: function () {
      if (!refs.champ) return;
      refs.champ.value = '';
      ajusterHauteurChamp();
      majCompteur();
      majEnvoi();
    },

    /**
     * Annonce un message aux lecteurs d'écran sans l'afficher visuellement.
     * Le corps du fil est déjà en `aria-live` : n'utiliser ceci que pour ce
     * qui n'entre pas dans le fil (« Erdy rédige… », reconnexion).
     * @param {string} message
     */
    annoncer: function (message) {
      if (refs.statut) refs.statut.textContent = message || '';
    },

    /* --- Registre et bus ------------------------------------------------------ */

    enregistrer: enregistrer,
    module: module,
    ecouter: ecouter,
    emettre: emettre,
  };

  /* ==========================================================================
     8. COMPORTEMENTS DE LA COQUILLE
     ====================================================================== */

  /** Le bouton d'envoi suit le champ : vide ou verrouillé ⇒ désactivé (F4/F8). */
  function majEnvoi() {
    if (!refs.envoyer || !refs.champ) return;
    if (hote && hote.dataset.saisie) return; /* Le verrou prime. */
    refs.envoyer.disabled = refs.champ.value.trim().length === 0;
  }

  /**
   * Compteur de caractères — planche F7. Il n'apparaît qu'à l'approche de la
   * limite : affiché en permanence, il ferait peser une contrainte sur une
   * conversation où 500 caractères sont très larges.
   *
   * ⚠️ Ce compteur n'est PAS un contrôle : le serveur revalide
   * (validation.ts, LONGUEUR_MAX_MESSAGE). Il informe, rien de plus.
   */
  function majCompteur() {
    if (!refs.compteur || !refs.champ) return;

    var max = reglages.maxCaracteres || 500;
    var longueur = refs.champ.value.length;
    var seuil = Math.round(max * 0.9);

    if (longueur < seuil) {
      refs.compteur.hidden = true;
      refs.compteur.textContent = '';
      return;
    }

    refs.compteur.hidden = false;
    refs.compteur.textContent = (textes.compteur || '%n% / %max%').replace('%n%', longueur).replace('%max%', max);
  }

  /**
   * Champ auto-extensible — planches F6 et B6. La hauteur est remise à zéro
   * avant mesure, sinon `scrollHeight` ne redescend jamais quand on efface.
   */
  function ajusterHauteurChamp() {
    if (!refs.champ) return;
    refs.champ.style.height = 'auto';
    refs.champ.style.height = refs.champ.scrollHeight + 'px';
  }

  /**
   * Soumission. Le socle ne fait que valider la forme et émettre : c'est le
   * module de transport qui orchestre le tour (dessine la bulle client via le
   * rendu, appelle le serveur, diffuse le flux). Voir docs/CONTRAT.md §4.
   */
  function soumettre(evenement) {
    if (evenement) evenement.preventDefault();
    if (!refs.champ) return;

    var texte = refs.champ.value.trim();
    if (!texte) return;
    if (hote && hote.dataset.saisie) return;

    /* Tronqué à la limite serveur plutôt que refusé : un message coupé part,
       un message refusé sans explication se perd. Le compteur a prévenu. */
    var max = reglages.maxCaracteres || 500;
    if (texte.length > max) texte = texte.slice(0, max);

    emettre('envoi', { texte: texte, idConversation: ChanvreryChat.idConversation() });
  }

  /* --- Délégation de clic ---------------------------------------------------- */

  function surClic(evenement) {
    var cible = evenement.target;
    if (!(cible instanceof Element)) return;

    if (cible.closest(SELECTEURS.accrocheRejeter)) {
      evenement.preventDefault();
      ChanvreryChat.rejeterAccroche();
      return;
    }
    if (cible.closest('[data-ch-chat-accroche-ouvrir]')) {
      ChanvreryChat.ouvrir('accroche');
      return;
    }
    if (cible.closest(SELECTEURS.lanceur)) {
      ChanvreryChat.basculer('lanceur');
      return;
    }
    if (cible.closest('[data-ch-chat-fermer]')) {
      ChanvreryChat.fermer();
      return;
    }
    if (cible.closest('[data-ch-chat-reduire]')) {
      ChanvreryChat.reduire();
      return;
    }
    if (cible.closest('[data-ch-chat-agrandir]')) {
      ChanvreryChat.basculerTaille();
      return;
    }
    if (cible.closest('[data-ch-chat-reinitialiser]')) {
      ChanvreryChat.nouvelleConversation();
      return;
    }
    /* L'en-tête entier redéploie un panneau réduit — planche B4 : la barre de
       titre est la seule chose visible, elle doit être cliquable en entier. */
    if (etatCourant === ETATS.REDUIT && cible.closest(SELECTEURS.entete)) {
      ChanvreryChat.restaurer();
    }
  }

  function surTouche(evenement) {
    if (evenement.key !== 'Escape') return;
    if (etatCourant !== ETATS.OUVERT && etatCourant !== ETATS.REDUIT) return;
    /* Rien de légalement bloquant ici, contrairement à la barrière 18+ :
       Échap referme, c'est le comportement attendu d'un panneau non modal. */
    ChanvreryChat.fermer();
  }

  /** Entrée envoie, Maj + Entrée va à la ligne — libellé d'aide de la planche F5. */
  function surToucheChamp(evenement) {
    if (evenement.key !== 'Enter' || evenement.shiftKey) return;
    evenement.preventDefault();
    soumettre(null);
  }

  /* --- Connexion / déconnexion réseau (G1, F9, G9) ---------------------------- */

  function surLigne() {
    if (hote) hote.dataset.reseau = 'en-ligne';
    emettre('reseau', { enLigne: true });
  }

  function surHorsLigne() {
    if (hote) hote.dataset.reseau = 'hors-ligne';
    emettre('reseau', { enLigne: false });
  }

  /* ==========================================================================
     9. ÉLÉMENT PERSONNALISÉ
     ====================================================================== */

  function lireReglages(element) {
    var jours = parseFloat(element.dataset.joursAccroche);
    var delai = parseFloat(element.dataset.delaiAccroche);
    var max = parseInt(element.dataset.maxCaracteres, 10);

    return {
      /** Origine du serveur de chat, sans barre oblique finale. */
      urlServeur: (element.dataset.urlServeur || '').replace(/\/+$/, ''),
      /** Code de langue passé à POST /chat. Le serveur reste libre de redétecter. */
      langue: element.dataset.langue || 'fr',
      maxCaracteres: isNaN(max) ? 500 : max,
      /** Secondes d'inactivité avant l'accroche proactive — 20 dans le relevé. */
      delaiAccroche: isNaN(delai) ? 20 : delai,
      joursAccroche: isNaN(jours) ? 7 : jours,
      /** L'accroche n'est prévue que sur fiche produit (règle du relevé A3). */
      accrocheProduitSeulement: element.dataset.accrocheProduit === 'true',
      /** `request.page_type` du rendu, utile aux modules pour le contexte. */
      typePage: element.dataset.typePage || '',
    };
  }

  function lireTextes(element) {
    var bloc = element.querySelector('[data-ch-chat-textes]');
    if (!bloc) return {};
    try {
      return JSON.parse(bloc.textContent) || {};
    } catch (e) {
      console.error('[Chanvrery · chat] bloc de libellés illisible', e);
      return {};
    }
  }

  class ChChatLeaElement extends HTMLElement {
    connectedCallback() {
      /* Une seule instance pilote le widget. Dans l'éditeur de thème, une
         section peut être rendue deux fois le temps d'un rechargement : la
         seconde s'efface au lieu de dédoubler le lanceur. */
      if (hote && hote !== this && document.contains(hote)) {
        this.hidden = true;
        return;
      }

      hote = this;
      reglages = lireReglages(this);
      textes = lireTextes(this);

      refs = {};
      Object.keys(SELECTEURS).forEach(
        function (cle) {
          refs[cle] = this.querySelector(SELECTEURS[cle]);
        }.bind(this),
      );

      this.dataset.etat = etatCourant;
      this.dataset.taille = tailleCourante;
      this.dataset.reseau = navigator.onLine === false ? 'hors-ligne' : 'en-ligne';

      this.addEventListener('click', surClic);
      document.addEventListener('keydown', surTouche);
      window.addEventListener('online', surLigne);
      window.addEventListener('offline', surHorsLigne);

      if (refs.formulaire) refs.formulaire.addEventListener('submit', soumettre);
      if (refs.champ) {
        refs.champ.addEventListener('input', function () {
          ajusterHauteurChamp();
          majCompteur();
          majEnvoi();
        });
        refs.champ.addEventListener('keydown', surToucheChamp);
      }

      majEnvoi();
      this.programmerAccroche();

      /* Tout est en place : les modules peuvent se brancher. Rémanent. */
      emettre('pret', { racine: this, refs: refs, reglages: reglages, textes: textes });
    }

    disconnectedCallback() {
      if (hote !== this) return;
      window.clearTimeout(minuterieAccroche);
      if (this.arreterAccroche) this.arreterAccroche();
      document.removeEventListener('keydown', surTouche);
      window.removeEventListener('online', surLigne);
      window.removeEventListener('offline', surHorsLigne);
      hote = null;
    }

    /**
     * Planche A3 : « après 20 s d'inactivité sur une fiche produit, une seule
     * fois par session ». Le délai et la restriction à la fiche produit sont
     * des réglages de section — le marchand doit pouvoir couper l'accroche
     * sans développeur.
     */
    programmerAccroche() {
      if (!reglages.delaiAccroche) return;
      if (reglages.accrocheProduitSeulement && reglages.typePage !== 'product') return;
      if (lireCookie(COOKIE_ACCROCHE) === 'rejetee') return;

      /* Toute action du visiteur repousse l'accroche : elle est censée
         récompenser une hésitation, pas interrompre une lecture active.
         Les écouteurs se retirent d'eux-mêmes dès que l'accroche a été vue ou
         que le panneau s'est ouvert — sur une fiche produit longue, trois
         écouteurs de défilement qui survivent à leur objet finissent par se
         payer en fluidité. */
      var evenements = ['pointerdown', 'keydown', 'scroll'];

      var arreter = function () {
        window.clearTimeout(minuterieAccroche);
        evenements.forEach(function (nom) {
          document.removeEventListener(nom, repousser);
        });
      };

      var planifier = function () {
        window.clearTimeout(minuterieAccroche);
        minuterieAccroche = window.setTimeout(function () {
          ChanvreryChat.montrerAccroche();
          arreter();
        }, reglages.delaiAccroche * 1000);
      };

      var repousser = function () {
        if (memoire.accrocheVue || etatCourant !== ETATS.FERME) {
          arreter();
          return;
        }
        planifier();
      };

      evenements.forEach(function (nom) {
        document.addEventListener(nom, repousser, { passive: true });
      });

      planifier();

      /* Retenu pour disconnectedCallback : une section retirée dans l'éditeur
         de thème ne doit rien laisser derrière elle. */
      this.arreterAccroche = arreter;
    }
  }

  window.ChanvreryChat = ChanvreryChat;

  if (!window.customElements.get('ch-chat-lea')) {
    window.customElements.define('ch-chat-lea', ChChatLeaElement);
  }
})();
